/**
 * Room directory.
 *
 * Lists every zone with who is in it, and jumps you there on click. Walking is
 * still the default way to move — this is for when you know where you need to
 * be and the office is bigger than your patience.
 */

import type { Zone } from '@virtual-office/shared';

import type { Store } from '../state.js';
import { $, clear, el } from './dom.js';

export class RoomsPanel {
  private readonly panel = $('rooms');
  private readonly list = $('rooms-list');

  constructor(
    private readonly store: Store,
    private readonly onNavigate: (zoneId: string) => void,
  ) {
    $('rooms-close').addEventListener('click', () => this.close());

    this.store.on('welcome', () => this.render());
    // Occupancy changes as people walk around.
    this.store.on('players', () => {
      if (this.isOpen) this.render();
    });
    // An admin may lock or unlock a room while this is open.
    this.store.on('access', () => this.render());
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.panel.hidden = false;
    this.render();
  }

  close(): void {
    this.panel.hidden = true;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  private occupantsOf(zone: Zone): string[] {
    return [...this.store.players.values()]
      .filter((p) => p.zoneId === zone.id)
      .map((p) => p.name)
      .sort((a, b) => a.localeCompare(b));
  }

  private render(): void {
    const map = this.store.map;
    if (!map) return;
    const here = this.store.me?.zoneId ?? null;

    clear(this.list);
    for (const zone of map.zones) {
      const occupants = this.occupantsOf(zone);
      const isHere = zone.id === here;

      const allowed = this.store.canEnter(zone.id);
      const row = el('button', { className: 'room-row' });
      row.type = 'button';
      if (isHere) row.classList.add('current');
      if (!allowed) row.classList.add('locked');

      const heading = el('div', { className: 'room-heading' }, [
        el('span', { className: 'room-name', text: allowed ? zone.name : `${zone.name} 🔒` }),
        el('span', {
          className: 'room-count',
          text: occupants.length === 0 ? 'empty' : `${occupants.length} here`,
        }),
      ]);

      // Closed rooms carry voices to everyone inside; the commons does not.
      const audio = zone.audio.mode === 'zone' ? 'whole room hears you' : 'nearby only';
      const access = this.store.access.get(zone.id);
      const lockReason =
        access?.minRole === 'admin' ? 'Admins only' : 'You are not on the list for this room';
      const detail = el('div', {
        className: 'room-detail',
        text: !allowed ? lockReason : occupants.length > 0 ? occupants.join(', ') : audio,
      });

      row.append(heading, detail);

      if (isHere) {
        row.disabled = true;
        row.append(el('span', { className: 'room-badge', text: "you're here" }));
      } else if (!allowed) {
        row.disabled = true;
      } else {
        row.addEventListener('click', () => {
          this.onNavigate(zone.id);
          this.close();
        });
      }

      this.list.append(row);
    }
  }
}
